import React, { useEffect } from "react";

import {
  TicketFormCardContainer,
  TitleTypography,
  FormContainer,
  FieldsContainer,
  TitleBadgeContainer,
  TicketFormHeader,
  TermsTooltipSpan
} from "./TicketFormCard.styles";
import {
  MeshGradient,
  MeshColours,
  Badge,
  TextField,
  SelectField,
  SelectWithTags,
  BadgeStylesType,
  Tooltip,
  InfoMessage,
} from "..";
import { IBookingTicket } from "../../models/BookingTicket";
import { SelectChangeEvent, useTheme } from "@mui/material";
import { emailValidation } from "../../utils/regex";
import { Icon } from "../../icons";
import { Variants } from "./TicketFormCard.variants";
import { TooltipName } from "../Tooltip/TooltipContent";
import { accessibilityOptions } from "src/models/Accessibility";

export interface CustomTicketFormCardProps {
  variant?: "ticket" | "unstyled";
  bookingTicket?: IBookingTicket;
  handleFormChange?: Function;
  ticketNumber: number;
  index: number;
  disabled: boolean;
  disableAccessibilityDropdown?: boolean;
  disabledIconTooltip?: TooltipName;
  colour?: MeshColours;
  isPriority?: boolean;
  minAge?: number;
  loggedInUserEmail?: string;
  validate?: () => void;
}

export const TicketFormCard = React.forwardRef(({
  variant = "ticket",
  bookingTicket,
  handleFormChange,
  ticketNumber,
  index,
  disabled = false,
  disableAccessibilityDropdown = false,
  disabledIconTooltip,
  colour = "pink-purple-green",
  isPriority = false,
  minAge = 0,
  loggedInUserEmail,
  ...rest
}: CustomTicketFormCardProps, ref) => {
  const [id, setId] = React.useState(bookingTicket?.id);
  const [firstName, setFirstName] = React.useState(bookingTicket?.firstName);
  const [lastName, setLastName] = React.useState(bookingTicket?.lastName);
  const [email, setEmail] = React.useState(bookingTicket?.email);
  const [age, setAge] = React.useState(bookingTicket?.age);
  const [accessibilityVal, setAccessibilityVal] = React.useState(
    bookingTicket?.accessibility === null
      ? "None"
      : bookingTicket?.accessibility ?? ""
  );
  const [accessibilityIssue, setAccessibilityIssue] = React.useState(bookingTicket?.otherAccessibility);

  const [firstNameError, setFirstNameError] = React.useState(false);
  const [lastNameError, setLastNameError] = React.useState(false);
  const [emailError, setEmailError] = React.useState(false);
  const [ageError, setAgeError] = React.useState(false);
  const [accessibilityError, setAccessibilityError] = React.useState(false);

  const theme = useTheme();

  const variantStylesFn = Variants(theme);

  const validateAll = () => {
    setFirstNameError(firstName === "");
    setLastNameError(lastName === "");
    setAgeError(localValues.age == undefined || localValues.age == 0);
    setEmailError(age != undefined && age >= 18 && (email != undefined && (email === "" || !emailValidation(email) || loggedInUserEmail === email)));
    setAccessibilityError(localValues.accessibility == "" || (localValues.accessibility == "Other" && localValues.otherAccessibility == ""));
  }

  React.useImperativeHandle(ref, () => ({
    validate: validateAll,
  }), [validateAll]);

  useEffect(() => {
    setId(bookingTicket?.id);
    setFirstName(bookingTicket?.firstName);
    setLastName(bookingTicket?.lastName);
    setEmail(bookingTicket?.email)
    setAge(bookingTicket?.age);
    setAccessibilityVal(bookingTicket?.accessibility ?? "None");
    setAccessibilityIssue(bookingTicket?.otherAccessibility);
  }, [bookingTicket]);

  const DisabledIconWithTooltip = () => {
    return disabledIconTooltip ? (
      <Tooltip tooltipName={disabledIconTooltip}>
        <Icon
          icon="LockClosed"
          size="sm"
          sx={(theme) => ({ color: theme.colors["neutral.50"] })}
        />
      </Tooltip>
    ) : (
      <Icon
        icon="LockClosed"
        size="sm"
        sx={(theme) => ({ color: theme.colors["neutral.50"] })}
      />
    );
  };

  let localValues = {
    id: id,
    firstName: firstName,
    lastName: lastName,
    email: email,
    age: age,
    accessibility: accessibilityVal,
    otherAccessibility: accessibilityIssue,
  } as IBookingTicket;

  const maxAge = 100;

  const ageOptions: Record<number, boolean> = {};
  Array.from(
    { length: maxAge - minAge + 1 },
    (_, index) => minAge + index
  ).forEach((a) => {
    ageOptions[a] = false;
  });

  const hasAccessibility =
      (bookingTicket?.accessibility && bookingTicket?.accessibility !== "None") || (bookingTicket?.otherAccessibility && bookingTicket?.otherAccessibility !== "");

  const ticketType: BadgeStylesType = isPriority
      ? "priority"
      : hasAccessibility
        ? "accessibilityRequired"
        : "generalAdmission";

  const isAccessibilityDisabled = disabled || disableAccessibilityDropdown;

  const AccessibilityDisabledIcon = () => {
    if (!disableAccessibilityDropdown) {
      return <DisabledIconWithTooltip />;
    }

      return (
        <Tooltip
          content={
            <>
              Accessibility Requirements cannot be edited currently. Please
              contact{" "}
              <a
                href="mailto:audience@thatstheticket.com.au"
                target="_blank"
                rel="noopener noreferrer"
              >
                audience@thatstheticket.com.au
              </a>{" "}
              with any changes needed.
            </>
          }
        >
          <Icon
            icon="LockClosed"
            size="sm"
            sx={(theme) => ({ color: theme.colors["neutral.50"] })}
          />
        </Tooltip>
      );
  };

  return (
    <TicketFormCardContainer data-cy="interest-form-container" id={`bookingForm-${index}`} sx={{...variantStylesFn[variant]}} {...rest} >
      <TicketFormHeader>
        <TitleBadgeContainer>
          <TitleTypography>Ticket {ticketNumber}</TitleTypography>
          {ticketNumber === 1 && <Badge badgeStyle="leadBooker" />}
          {bookingTicket && bookingTicket?.age != 0 && bookingTicket?.age < 18 && <Badge badgeStyle="underAge" />}
        </TitleBadgeContainer>

        <Badge badgeStyle={ticketType} showLabel />
      </TicketFormHeader>

      <FormContainer>
        <FieldsContainer>
          <TextField
            placeholder="First Name"
            size="md"
            variant="secondary"
            required
            error={firstNameError}
            errorMessage="Field Required."
            errorBackground
            data-cy="interest-form-first-name"
            value={
              bookingTicket?.firstName ? bookingTicket?.firstName : firstName
            }
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              if (!disabled && handleFormChange) {
                const value = event.target.value as string;
                setFirstName(value);
                localValues.firstName = value;
                setFirstNameError(value === "");
                handleFormChange(localValues, index);
              }
            }}
            onBlurCapture={() => {
              setFirstNameError(firstName === "");
            }}
            fullWidth
            disabled={disabled}
            disabledIcon={<DisabledIconWithTooltip />}
          />
          <TextField
            placeholder="Last Name"
            size="md"
            variant="secondary"
            required
            error={lastNameError}
            errorMessage="Field Required."
            errorBackground
            data-cy="interest-form-last-name"
            value={bookingTicket?.lastName ? bookingTicket?.lastName : lastName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              if (!disabled && handleFormChange) {
                const value = event.target.value as string;
                setLastName(value);
                localValues.lastName = value;
                setLastNameError(value === "");
                handleFormChange(localValues, index);
              }
            }}
            onBlurCapture={() => {
              setLastNameError(lastName === "");
            }}
            fullWidth
            disabled={disabled}
            disabledIcon={<DisabledIconWithTooltip />}
          />
        </FieldsContainer>
        {age != undefined && age >= 18 &&
          <FieldsContainer>
            <TextField
              placeholder="Guest’s Email Address"
              size="md"
              variant="secondary"
              required
              error={emailError}
              errorMessage={
                !disabled && loggedInUserEmail === email && email !== ""
                  ? "Please enter guest’s email, not your own"
                  :  email !== "" && !emailValidation(String(email))
                  ? "Invalid Email"
                  : "Field Required"
              }
              errorBackground
              data-cy="interest-form-email"
              value={bookingTicket?.email ? bookingTicket?.email : email}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                if (!disabled && handleFormChange) {
                  const value = event.target.value as string;
                  setEmail(value);
                  localValues.email = value;
                  setEmailError(
                    age != undefined &&
                      age >= 18 &&
                      (value === "" ||
                        loggedInUserEmail === value ||
                        !emailValidation(value))
                  );
                  handleFormChange(localValues, index);
                }
              }}
              onBlurCapture={() => {
                setEmailError(
                  age != undefined &&
                    age >= 18 &&
                    email != undefined &&
                    (email === "" ||
                      !emailValidation(email) ||
                      loggedInUserEmail === email)
                );
              }}
              fullWidth
              disabled={disabled}
              disabledIcon={<DisabledIconWithTooltip />}
            />
          </FieldsContainer>
        }
        <FieldsContainer>
          <SelectField
            id={`age-${index}`}
            data-cy="interest-form-age"
            value={bookingTicket?.age && bookingTicket?.age > 0 ? bookingTicket?.age : ""}
            label="Age"
            infoMessage={minAge && minAge != 0 ? `The minimum age for this show is ${minAge}` : "No minimum age for this show" }
            onChange={(event: SelectChangeEvent<unknown>) => {
              if (!disabled && handleFormChange) {
                const value = Number(event.target.value);
                setAge(value);
                localValues.age = value;
                if(localValues.age < 18) {
                  setEmail("")
                  localValues.email = "";
                }
                handleFormChange(localValues, index);
              }
            }}
            disabledIcon={<DisabledIconWithTooltip />}
            error={ageError}
            errorMessage="Field Required"
            errorBackground
            onBlurCapture={() => {
              setAgeError(localValues.age == undefined || localValues.age == 0);
            }}
            options={ageOptions}
            disabled={disabled}
            placeholder="--"
            size="md"
            type="numbersOnly"
            variant="secondary"
          />
          <SelectWithTags
            id={`accessibility-${index}`}
            data-cy="interest-form-accessibility"
            value={bookingTicket?.accessibility ?? accessibilityVal}
            other={(bookingTicket?.otherAccessibility ?? accessibilityIssue) ?? ""}
            doneButton
            fullWidth
            label="Accessibility Requirements?"
            placeholder="You Must Select One"
            sx={{ maxWidth: variant !== "unstyled" ? "14.25rem" : "unset" }}
            error={accessibilityError}
            errorMessage="Select Accessibility Requirements, even if the answer is ‘None’"
            errorBackground
            onBlurCapture={() => {
              if (!isAccessibilityDisabled) {
                setAccessibilityError(localValues.accessibility == "" || (localValues.accessibility == "Other" && localValues.otherAccessibility == ""));
              }
            }}
            onChange={(newValue) => {
              if (!isAccessibilityDisabled && handleFormChange) {
                const otherAccessibilitySelected =
                    newValue && Array.isArray(newValue)
                      ? newValue.filter(item => (item.manualInput || !accessibilityOptions[item.value])).map((item) => item.value)
                      : newValue?.manualInput
                      ? [newValue]
                      : [];

                const value =
                    newValue && Array.isArray(newValue)
                      ? newValue.map((item) => item.value)
                      : newValue?.value;

                let valueString = Array.isArray(value) ? value[0] : value
                valueString = valueString?.trim() || ""

                if (otherAccessibilitySelected.length) {
                  setAccessibilityVal("Other");
                  localValues.accessibility = "Other";

                  setAccessibilityIssue(valueString)
                  localValues.otherAccessibility = valueString;
                } else {
                  setAccessibilityVal(valueString);
                  localValues.accessibility = valueString;

                  setAccessibilityIssue(null)
                  localValues.otherAccessibility = null;
                }

                handleFormChange(localValues, index);
              }
            }}
            options={accessibilityOptions}
            variant="secondary"
            disabled={isAccessibilityDisabled}
            disabledIcon={<AccessibilityDisabledIcon />}
          />
        </FieldsContainer>
        {age != undefined && age >= 18 && !disabled && (
          <InfoMessage
            hasBackground={true}
            hasIcon={true}
            variant="info"
            message={
              <div>
                Make sure you enter the guest’s email address (not yours)
                because they must{" "}
                <Tooltip tooltipName="acceptTerms">
                  <TermsTooltipSpan> accept the terms & conditions </TermsTooltipSpan>
                </Tooltip>{" "}
                of the show themselves.
              </div>
            }
          />
        )}
      </FormContainer>
      {variant !== "unstyled" && <MeshGradient colour={colour} intensity="high" sx={{zIndex: "0"}} position="right" size="high" />}
    </TicketFormCardContainer>
  );
});

export interface TicketFormCardProps
  extends React.ComponentProps<typeof TicketFormCard> {
  as?: React.ElementType;
}

export default React.memo(TicketFormCard);
